import Link from "next/link";
import type { PublicCatalogItem } from "@bash/shared";
import { isIconUrl } from "@/lib/abilities/icon";

interface PublicCardProps {
  item: PublicCatalogItem;
  /** Plural URL segment: 'skills' | 'agents' | 'abilities' */
  type: string;
}

export function PublicCard({ item, type }: PublicCardProps) {
  const color = item.color ?? "var(--fg-faint)";
  const detailHref = `/${type}/${item.slug}`;
  const loginHref = `/login?next=${encodeURIComponent(
    "/" + type + "/" + item.slug
  )}`;

  const toolCount = item.tools?.length ?? 0;
  const mcpCount = item.mcps?.length ?? 0;

  return (
    <div
      className="group relative isolate overflow-hidden rounded-20 border border-border-subtle bg-bg-elevated p-5.5 min-h-52 flex flex-col transition-all duration-300 hover:-translate-y-[3px] hover:border-border-muted hover:shadow-marketplace-grid"
      style={{
        backgroundImage: `linear-gradient(135deg, color-mix(in srgb, ${color} 9%, transparent), color-mix(in srgb, ${color} 3%, transparent))`,
      }}
    >
      {/* Corner glow */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-24 z-[-1] h-80 w-80 rounded-full blur-[40px] opacity-5"
        style={{
          background: `radial-gradient(closest-side, ${color}, transparent 70%)`,
        }}
      />

      {/* Icon tile (top-right). Icons can be an emoji/short string OR a logo
          URL (e.g. Composio integrations: https://logos.composio.dev/...). For
          URLs render an <img>; otherwise render the glyph as text. */}
      {item.icon && (
        <span
          aria-hidden="true"
          className="absolute top-5.5 right-5.5 flex h-12 w-12 items-center justify-center overflow-hidden rounded-16 border border-border-subtle text-5"
          style={{
            background: `color-mix(in srgb, ${color} 16%, var(--bg-elevated, hsl(0 0% 10%)))`,
          }}
        >
          {isIconUrl(item.icon) ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={item.icon} alt="" className="h-7 w-7 object-contain" />
          ) : (
            item.icon
          )}
        </span>
      )}

      {/* Category pill */}
      {item.category && (
        <div className="mb-3 flex items-center gap-2">
          <span className="inline-block rounded-full border border-border-subtle bg-bg-base/40 px-2 py-1 text-2.5 font-semibold uppercase tracking-[0.16em] text-fg-subtle">
            {item.category}
          </span>
        </div>
      )}

      {/* Title — Link navigates to detail page; plain sibling to CTA (no nesting) */}
      <h3 className="text-5.5 font-semibold tracking-[-0.022em] leading-[1.1] text-fg-default">
        <Link href={detailHref} className="hover:underline">
          {item.name}
        </Link>
      </h3>

      {/* Description */}
      <p className="mt-2 text-3.5 leading-[1.55] text-fg-muted line-clamp-2 max-w-[50ch]">
        {item.description}
      </p>

      {/* Inventory chips */}
      {(toolCount > 0 || mcpCount > 0) && (
        <div className="mt-3.5 flex flex-wrap items-center gap-1.5">
          {toolCount > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-bg-base/45 px-2.5 py-1 text-3 font-medium text-fg-default tabular-nums">
              <span>{toolCount}</span>
              <span className="text-fg-subtle">tools</span>
            </span>
          )}
          {mcpCount > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-bg-base/45 px-2.5 py-1 text-3 font-medium text-fg-default tabular-nums">
              <span>{mcpCount}</span>
              <span className="text-fg-subtle">MCPs</span>
            </span>
          )}
        </div>
      )}

      {/* Hover-reveal CTA — sibling to title Link, absolutely positioned, higher z-index */}
      <Link
        href={loginHref}
        className="absolute bottom-5.5 right-5.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity inline-flex h-8 items-center rounded-full bg-fg-default px-3.5 text-3.5 font-semibold text-fg-inverse whitespace-nowrap"
      >
        Sign in to install
      </Link>
    </div>
  );
}
